import { OPEN_OFFER_POLICY, weekdayNameFromIndex } from '@yourstore/domain';
import { addDays, dayTimeToUtc, toIsoDay, weekdayIndex } from './timezone.js';
/**
 * **What the house is willing to offer** — the three knobs of
 * `docs/lovespa-gaps-plan.md` A2, A3 and A4, as pure functions over plain data.
 *
 * Everything here is structural on purpose: not one function takes a
 * `SchedulingContext`, so this file can be imported by `availability.ts`
 * without a cycle, called by the operator console's simulator on a day an
 * operator typed in, and reasoned about one rule at a time.
 *
 * ## The one rule each
 *
 *  - **A2 — the gap.** `buffersFor` is the only place a service's effective
 *    buffers are decided. dev.md rule 3's shape one noun over: a second answer
 *    to "how much room does this appointment take" would make the offered span,
 *    the occupied span the exclusion constraint sees, and the ledger's
 *    admission three opinions, and two of them would be wrong.
 *  - **A3 — the lead.** `offerFloorMs` is the only place "the earliest instant
 *    we will name" is decided. It is the same floor `ctx.now` has always been,
 *    moved forward for everybody except the hand that owns the book.
 *  - **A4 — the shape.** `judgeCandidate` is the only place a candidate start
 *    is kept or dropped by policy, and it is a **pure predicate over geometry**
 *    — gaps, edges, anchors — so the console can show an operator the reason
 *    beside the time, in her own day, before she picks a mode.
 */
const MINUTE = 60_000;
/**
 * **The one place a service's buffers are resolved.**
 *
 * The rule is one sentence and it is the owner's: *"I do a half an hour between
 * clients"*, always — unless this particular treatment says otherwise. So **an
 * item that carries its own number keeps it, and an item that carries `null`
 * takes the house's default.**
 *
 * `0` is a number. Before migration 0062 it could not be: the column was
 * `NOT NULL DEFAULT 0`, so a deliberate "no gap after this one" and a column
 * nobody had touched were the same bytes, and the only honest reading was
 * *"zero takes the default"* — which meant an owner who kept a half hour
 * everywhere could not pack one quick treatment tight. The migration backfilled
 * every `0` to `NULL`, because `0` had only ever meant "never said", and this
 * function reads the two apart: `null → the house's; a number → that number`.
 *
 * Absent defaults resolve a `null` to `0`, and a number to itself **by identity
 * of value**, which is why `buffers-and-lead.test.ts` can assert that a fleet
 * whose items all carry `null` produces byte-identical answers to the fleet
 * that carried `0` the day before the migration.
 */
export function buffersFor(policy, service) {
    const before = service.buffer_before_min;
    const after = service.buffer_after_min;
    return {
        before_min: before ?? policy?.default_buffer_before_min ?? 0,
        after_min: after ?? policy?.default_buffer_after_min ?? 0,
    };
}
/**
 * **The span a booking actually takes off the resource**: the sold span plus
 * the resolved buffers.
 *
 * This is the span the exclusion constraint on `appointment_resources` sees, so
 * every surface that writes one owes its arithmetic to this function rather
 * than to `item.buffer_after_min * 60_000` — `packages/store`'s `createBooking`
 * and `rescheduleBooking`, `apps/provider-api`'s hold, and `apps/core-api`'s
 * conflict search all derive the same span today from the item alone, which
 * stops being right the moment a tenant sets a default. Epoch milliseconds in
 * and out, so a caller holding `Date`s converts at its own edge and this
 * function keeps one vocabulary.
 */
export function occupiedSpan(policy, service, startMs, endMs) {
    const buffers = buffersFor(policy, service);
    return {
        start: startMs - buffers.before_min * MINUTE,
        end: endMs + buffers.after_min * MINUTE,
    };
}
/**
 * **The earliest instant this ask may be offered, exclusive.**
 *
 * The engine has always had one floor — never a time that has already gone —
 * and it has always been `ctx.now`. A3 moves it forward, for everybody except
 * the hand that owns the book:
 *
 *  - `min_lead_minutes` pushes it out by that much. *"I like being able to tell
 *    people I'm booked for two weeks."*
 *  - `same_day_cutoff` is a local `HH:MM`: once the tenant's own clock has
 *    passed it, **today stops being offered at all** and the floor jumps to the
 *    start of tomorrow, local. She answers her texts at the end of the business
 *    day, so a four-o'clock slot sold at half past three is a slot nobody told
 *    her about.
 *
 * Exclusive, and the `- 1` is deliberate rather than sloppy. `ctx.now` itself
 * is the *past* — a start at exactly now has gone — but a start at exactly
 * `now + min_lead_minutes`, or at exactly midnight after a cut-off, is
 * precisely what the owner said she would take. One millisecond is the whole of
 * that distinction, and writing it here means the grid slicer keeps its single
 * `after` parameter.
 *
 * Read in the **tenant's** zone through the same two-pass converter every other
 * wall-clock reading uses, so a DST boundary between now and midnight lands on
 * the right instant rather than an hour out.
 */
export function offerFloorMs(policy, houseRules) {
    if (!houseRules)
        return policy.now;
    let floor = policy.now;
    const lead = policy.min_lead_minutes ?? 0;
    if (lead > 0)
        floor = Math.max(floor, policy.now + lead * MINUTE - 1);
    const cutoff = policy.same_day_cutoff;
    if (cutoff !== undefined && cutoff !== '') {
        const today = toIsoDay(policy.timezone, policy.now);
        if (policy.now >= dayTimeToUtc(policy.timezone, today, cutoff)) {
            floor = Math.max(floor, dayTimeToUtc(policy.timezone, addDays(today, 1), '00:00') - 1);
        }
    }
    return floor;
}
/**
 * The stretches of one calendar day that are **not offered to the public**.
 *
 * Returned as ordinary intervals so the slicer can merge them in beside the
 * blackouts and the busy time: a protected window behaves like a closure for a
 * guest or a partner agent, and like nothing at all for a member of staff. That
 * asymmetry is the entire point — a blackout row would take her lunch hour away
 * from *her* too, and the one thing she does with it is decide, now and then,
 * to give it to somebody.
 *
 * Days are matched by **name**, Monday-first, through the same
 * `weekdayNameFromIndex` the reminders config uses.
 */
export function protectedIntervalsOn(policy, isoDay, houseRules) {
    if (!houseRules)
        return [];
    const windows = policy.offer_policy?.protected ?? [];
    if (windows.length === 0)
        return [];
    const name = weekdayNameFromIndex(weekdayIndex(isoDay));
    const out = [];
    for (const window of windows) {
        if (!window.days.includes(name))
            continue;
        const start = dayTimeToUtc(policy.timezone, isoDay, window.start);
        const end = dayTimeToUtc(policy.timezone, isoDay, window.end);
        // An `end` at or before `start` is a misconfigured window and not an
        // overnight one, exactly as `openOnDay` reads a misconfigured shift.
        if (end > start)
            out.push({ start, end });
    }
    return out;
}
/**
 * **Keep this start, or not** — the whole of A4's filter, as one pure predicate.
 *
 * `open` keeps everything, which is what the engine has always done.
 *
 * `no_slivers` drops a start whose leftover free time on either side is
 * *greater than nothing and shorter than* `minUsefulGapMs`. Both halves matter:
 * a leftover of exactly zero is a start that abuts something and is the best
 * kind there is, and a leftover longer than the shortest thing she sells is a
 * gap she can still fill. A twenty-minute hole is neither.
 *
 * `touching` keeps a start whose occupied span abuts an existing occupied span
 * or the edge of its open window, within one grid step — *"after this one or
 * before the other one, not somewhere in the middle, because then your day is
 * destroyed"*. On a resource-day with **nothing booked**, there is nothing to
 * abut and the rule would collapse to "the opening, and nothing else", so the
 * anchors decide instead; `OfferGeometry.anchor` carries that decision.
 */
export function judgeCandidate(mode, geometry, minUsefulGapMs) {
    const abutsBefore = geometry.gap_before_ms <= geometry.grid_ms;
    const abutsAfter = geometry.gap_after_ms <= geometry.grid_ms;
    const score = abutsBefore && abutsAfter ? 3 : abutsBefore || abutsAfter ? 2 : geometry.anchor ? 1 : 0;
    const base = { abuts_before: abutsBefore, abuts_after: abutsAfter, score };
    if (geometry.pooled)
        return { kept: true, reason: 'pool', ...base };
    if (mode === 'open')
        return { kept: true, reason: 'offered', ...base };
    if (mode === 'no_slivers') {
        if (geometry.gap_before_ms > 0 && geometry.gap_before_ms < minUsefulGapMs) {
            return { kept: false, reason: 'sliver_before', ...base };
        }
        if (geometry.gap_after_ms > 0 && geometry.gap_after_ms < minUsefulGapMs) {
            return { kept: false, reason: 'sliver_after', ...base };
        }
        return { kept: true, reason: 'offered', ...base };
    }
    // touching
    if (geometry.day_empty) {
        return geometry.anchor
            ? { kept: true, reason: 'offered', ...base }
            : { kept: false, reason: 'not_an_anchor', ...base };
    }
    return abutsBefore || abutsAfter
        ? { kept: true, reason: 'offered', ...base }
        : { kept: false, reason: 'not_touching', ...base };
}
/* ------------------------------------------------------------------ */
/* The ladder                                                          */
/* ------------------------------------------------------------------ */
/**
 * The relaxation ladder for one mode: itself, then everything looser.
 *
 * The engine walks it and stops at the first rung that offers something, which
 * is invariant 3 (`alternatives` is never empty while the horizon has anything)
 * generalized from days to policies — and, before that, plain good business. A
 * rule that answered "nothing available" on a Tuesday with three free hours in
 * it because none of them touched anything is a rule an owner switches off
 * within a week, and then we have neither the rule nor her trust in the thing
 * that offered it.
 *
 * Which rung actually answered comes back on the result as `policy_applied`, so
 * a caller can tell "she would have preferred not to offer this" from "this is
 * what she wants offered".
 */
export function relaxationLadder(mode) {
    if (mode === 'touching')
        return ['touching', 'no_slivers', 'open'];
    if (mode === 'no_slivers')
        return ['no_slivers', 'open'];
    return ['open'];
}
/** The policy in force, with the staff exemption already applied. */
export function effectiveOfferPolicy(policy, houseRules) {
    if (!houseRules || policy === undefined)
        return OPEN_OFFER_POLICY;
    return policy;
}
//# sourceMappingURL=offers.js.map